import { ButtonAsWrapper, FlexRow, TextDynamic } from '@minecraft.atoms';
import React, { FC } from 'react';
import Icon from '@minecraft.icon';
import ItemWithTooltip from '@minecraft.item-with-tooltip';

export type SocialMediaButtonProps = {
  id: string;
  'aria-label': string;
  iconName: string;
  disabled?: boolean;
  showTooltip?: boolean;
  label?: string;
  tooltipContent?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  isLarge?: boolean;
  onCloseClick?: () => void;
  wrapperClassName?: string;
};
type ButtonContentProps = Omit<SocialMediaButtonProps, 'showTooltip' | 'tooltipContent'>;

const ButtonContent: FC<ButtonContentProps> = ({
  iconName,
  disabled,
  label,
  onClick,
  'aria-label': ariaLabel,
  id,
  isLarge = false,
  onCloseClick,
  wrapperClassName,
}) => {
  return (
    <ButtonAsWrapper
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className="cn_w-full cn_pointer-events-auto"
      unsetSizing
      data-testid={`${id}-button`}
    >
      <FlexRow
        bgColor="cardLightContrast"
        borderRadius="sm"
        alignItems="center"
        justifyContent="center"
        className={wrapperClassName}
        gap="2"
        wrap="nowrap"
      >
        <Icon name={iconName} size={isLarge ? 'lg' : 'sm'} className={disabled ? 'cn_grayscale' : ''} />
        <TextDynamic
          color={disabled ? 'default' : 'primary'}
          className="cn_atom_ml-1"
          data-testid={`${id}-label`}
          fontSize={isLarge ? 'bodyRegular' : 'bodySmall'}
          fontWeight="medium"
          as="span"
        >
          {label}
        </TextDynamic>
        {onCloseClick && (
          <Icon
            name="close"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onCloseClick();
            }}
          />
        )}
      </FlexRow>
    </ButtonAsWrapper>
  );
};

/**
 * Props for the SocialMediaButton component.
 *
 * @property id - A unique identifier for the button to facilitate tooltip.
 * @property aria-label - Accessibility label for the button.
 * @property iconName - (Optional) Name of the icon to be displayed.
 * @property disabled - (Optional) If true, the button will be disabled.
 * @property showTooltip - (Optional) If true, a tooltip will be shown.
 * @property label - (Optional) Label text for the button.
 * @property tooltipContent - (Optional) Content to be displayed inside the tooltip.
 * @property onClick - (Optional) Click event handler for the button.
 * @property isLarge - (Optional) If true, the button will be large.
 * @property onCloseClick - (Optional) Click event handler for the close button.
 * @property wrapperClassName - (Optional) Additional class name for the wrapper.
 */

export const SocialMediaButton: FC<SocialMediaButtonProps> = ({
  iconName,
  id,
  disabled = false,
  label,
  tooltipContent,
  showTooltip = false,
  'aria-label': ariaLabel,
  onClick,
  isLarge = false,
  onCloseClick,
  wrapperClassName,
}) => {
  if (!id) {
    return null;
  }
  const content = (
    <ButtonContent
      iconName={iconName}
      onClick={onClick}
      label={label}
      disabled={disabled}
      aria-label={ariaLabel}
      id={id}
      isLarge={isLarge}
      onCloseClick={onCloseClick}
      wrapperClassName={wrapperClassName}
    />
  );

  return showTooltip ? (
    <ItemWithTooltip tooltipContent={tooltipContent} tooltipPlacement="top" id={`${id}-tooltip`} className="cn_w-full">
      {content}
    </ItemWithTooltip>
  ) : (
    content
  );
};
